import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../App";
import { API } from "../App";

const GREEN = "#1A4D2E";
const LEAF = "M24,40 C12,32 8,20 16,10 C20,4 28,4 32,10 C40,20 36,32 24,40Z";

function TaxBuddyLogo({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="13" fill={GREEN}/>
      <path d={LEAF} fill="white" opacity="0.95"/>
      <line x1="24" y1="38" x2="24" y2="12" stroke={GREEN} strokeWidth="2" strokeLinecap="round" opacity="0.45"/>
    </svg>
  );
}

export default function Login({ defaultTab = "login" }) {
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: "", password: "", full_name: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = tab === "login" ? "/api/auth/login" : "/api/auth/register";
      const payload = tab === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, full_name: form.full_name };

      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.access_token, res.data.user);
      toast.success(tab === "login" ? "Welcome back!" : "Account created — welcome to TaxBuddy!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setForm({ email: "demo@taxbuddy.com", password: "Demo123!", full_name: "" });
    setTab("login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "#F9FAFB", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <TaxBuddyLogo size={56} />
          </div>
          <h1 className="text-3xl font-black text-gray-900" style={{ letterSpacing: "-.4px" }}>TaxBuddy</h1>
          <p className="text-gray-500 mt-1 text-sm">Australian Tax Record Organiser</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            {["login", "register"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                  tab === t ? "bg-white shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
                style={tab === t ? { color: GREEN } : {}}
              >
                {t === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label>
                <input
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none transition focus:border-green-800 focus:ring-2 focus:ring-green-100"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none transition focus:border-green-800 focus:ring-2 focus:ring-green-100"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none transition focus:border-green-800 focus:ring-2 focus:ring-green-100 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {tab === "register" && (
                <p className="text-xs text-gray-400 mt-1">Minimum 8 characters</p>
              )}
              {tab === "login" && (
                <div className="text-right mt-1">
                  <Link to="/reset-password" className="text-xs hover:underline font-semibold" style={{ color: GREEN }}>
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-3 rounded-xl font-bold text-sm transition disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: GREEN }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {tab === "login" ? "Sign In" : "Create Free Account"}
            </button>
          </form>

          {/* Demo */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={fillDemo}
              className="w-full py-2 text-sm rounded-xl transition font-semibold hover:bg-green-50"
              style={{ color: GREEN }}
            >
              Try the demo account
            </button>
          </div>
        </div>

        {/* Public links */}
        <div className="flex justify-center gap-5 mt-5 text-xs text-gray-400">
          <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
          <Link to="/about" className="hover:text-gray-600 transition-colors">About</Link>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
        </div>

        <p className="text-center text-xs text-gray-400 mt-3 leading-relaxed px-4">
          TaxBuddy organises your records for tax time. It is not financial, tax, or legal advice.
          Always consult a registered tax agent before lodging your return.
        </p>
      </div>
    </div>
  );
}
